import { useState, useEffect } from "react";
import { useNavigate } from "react-router";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";

import { country, currentUser } from "./../../Config/Data";

import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import BackButton from "../../Components/BackButton";
import CustomInput from "../../Components/CustomInput";
import CustomButton from "../../Components/CustomButton";
import { SelectBox } from "../../Components/CustomSelect";
import CustomModal from "../../Components/CustomModal";
import { useGet, usePostForm } from "../../Api";
import { base_url } from "../../Api/apiConfig";
import { useNotification } from '../../Components/GlobalNotification';

import './style.css'

const EditProfile = () => {
  const { showNotification } = useNotification();

    const navigate = useNavigate()

    const [userData, setUserData] = useState({});
    const [form, setForm] = useState({
        username: "",
        email: "",
        first_name: "",
        last_name: "",
        display_name: "",
        phone: "",
        native_language: "",
        other_language: "",
        bio: "",
        address: {
            street_address: "",
            apt_suite: "",
            country_id: "",
            country: "",
            state_id: "",
            state: "",
            city_id: "",
            city: "",
            zip_code: "",
            neighborhood: "",
        },
    });
    const [optionData, setOptionData] = useState({});
    const [countriesList, setCountriesList] = useState([]);
    const [statesList, setStatesList] = useState([]);
    const [citiesList, setCitiesList] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const {
        ApiData: ProfileInfoData,
        loading: ProfileInfoLoading,
        error: ProfileInfoError,
        get: GetProfileInfo,
    } = useGet(`/profile-information`);

    const {
        ApiData: SaveProfileData,
        loading: SaveProfileLoading,
        error: SaveProfileError,
        post: PostProfileInfo,
    } = usePostForm(`/profile-information`);

    const {
        ApiData: ProfileAddressData,
        loading: ProfileAddressLoading,
        error: ProfileAddressError,
        get: GetProfileAddress,
    } = useGet(`/profile-addresses/show-address`);

    const {
        ApiData: SaveProfileAddressData,
        loading: SaveProfileAddressLoading,
        error: SaveProfileAddressError,
        post: PostProfileAddress,
    } = usePostForm(`/profile-addresses`);

    const handleClickPopup = () => {
        // Submit profile-information (form-data) on Save
        PostProfileInfo({
            username: form.username,
            first_name: form.first_name,
            last_name: form.last_name,
            display_name: form.display_name,
            phone: form.phone,
            native_language: form.native_language,
            other_language: form.other_language,
            bio: form.bio,
        });

        // Submit profile address
        const { address } = form;
        PostProfileAddress({
            street_address: address.street_address,
            apt_suite: address.apt_suite,
            country: address.country,
            state: address.state,
            city: address.city,
            zip_code: address.zip_code,
            neighborhood: address.neighborhood,
        });
    }

    const handleClose = () => {
        setShowModal(false);
        navigate('/profile')
    }


    // Load countries once on mount
    useEffect(() => {
        fetch(`${base_url}/countries`, { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((res) => {
                const raw = res?.data?.data ?? res?.data ?? res ?? [];
                const list = Array.isArray(raw) ? raw.map((item) => ({
                    id: item.id,
                    name: item.name ?? item.country_name ?? item.title ?? '',
                })) : [];
                setCountriesList(list);
            })
            .catch(() => setCountriesList([]));
    }, []);

    useEffect(() => {
        document.title = 'kvr User | Profile';
        setOptionData(country);
        setUserData(currentUser);
        GetProfileInfo();
        GetProfileAddress();

        // Bootstrap fallback values from the account created at sign-up
        try {
            const storedUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const storedUsername = localStorage.getItem('username');
            setForm((prev) => ({
                ...prev,
                username: prev.username || storedUsername || storedUserInfo?.name || "",
                email: prev.email || storedUserInfo?.email || "",
            }));
        } catch (e) {
            // ignore parsing errors
        }
    }, []);

    useEffect(() => {
        if (!ProfileInfoData) return;

        const infoFromApi = Array.isArray(ProfileInfoData) ? ProfileInfoData?.[0] : ProfileInfoData;

        const info =
            infoFromApi?.data ||
            infoFromApi?.profile ||
            infoFromApi?.user ||
            infoFromApi;

        // Prefer account-level username/email from localStorage when the profile
        // record does not yet contain them.
        const storedUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const storedUsername = localStorage.getItem('username');

        setForm((prev) => ({
            ...prev,
            username: info?.username || storedUsername || storedUserInfo?.name || prev.username || "",
            email: info?.email || storedUserInfo?.email || prev.email || "",
            first_name: info?.first_name ?? prev.first_name,
            last_name: info?.last_name ?? prev.last_name,
            display_name: info?.display_name ?? prev.display_name,
            phone: info?.phone ?? prev.phone,
            native_language: info?.native_language ?? prev.native_language,
            other_language: info?.other_language ?? prev.other_language,
            bio: info?.bio ?? prev.bio,
        }));
    }, [ProfileInfoData]);

    // Load existing profile address and resolve country/state/city IDs from names
    useEffect(() => {
        if (!ProfileAddressData) return;

        const addr =
            ProfileAddressData?.data ||
            ProfileAddressData?.profile_address ||
            ProfileAddressData;

        if (!addr || typeof addr !== 'object') return;

        const findIdByName = (list, name) => {
            if (!name || !Array.isArray(list)) return '';
            const found = list.find((item) => String(item.name).trim().toLowerCase() === String(name).trim().toLowerCase());
            return found ? found.id : '';
        };

        setForm((prev) => ({
            ...prev,
            address: {
                street_address: addr.street_address ?? prev.address?.street_address ?? '',
                apt_suite: addr.apt_suite ?? prev.address?.apt_suite ?? '',
                country: addr.country ?? prev.address?.country ?? '',
                country_id: findIdByName(countriesList, addr.country) || prev.address?.country_id || '',
                state: addr.state ?? prev.address?.state ?? '',
                state_id: '', // resolved below after states load
                city: addr.city ?? prev.address?.city ?? '',
                city_id: '', // resolved below after cities load
                zip_code: addr.zip_code ?? prev.address?.zip_code ?? '',
                neighborhood: addr.neighborhood ?? prev.address?.neighborhood ?? '',
            },
        }));
    }, [ProfileAddressData, countriesList]);

    // Fetch states when country is selected/resolved
    useEffect(() => {
        const countryId = form.address?.country_id;
        if (!countryId) {
            setStatesList([]);
            return;
        }
        fetch(`${base_url}/state/by-country?country=${countryId}`, { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((res) => {
                const raw = res?.data?.data ?? res?.data ?? res ?? [];
                const list = Array.isArray(raw) ? raw.map((item) => ({
                    id: item.id,
                    name: item.name ?? item.state_name ?? item.title ?? '',
                })) : [];
                setStatesList(list);

                // If we already have a state name, resolve its id
                const stateName = form.address?.state;
                if (stateName) {
                    const found = list.find((s) => String(s.name).trim().toLowerCase() === String(stateName).trim().toLowerCase());
                    if (found) {
                        setForm((prev) => ({
                            ...prev,
                            address: { ...prev.address, state_id: found.id },
                        }));
                    }
                }
            })
            .catch(() => setStatesList([]));
    }, [form.address?.country_id]);

    // Fetch cities when state is selected/resolved
    useEffect(() => {
        const stateId = form.address?.state_id;
        if (!stateId) {
            setCitiesList([]);
            return;
        }
        fetch(`${base_url}/city/by-state?state=${stateId}`, { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .then((res) => {
                const raw = res?.data?.data ?? res?.data ?? res ?? [];
                const list = Array.isArray(raw) ? raw.map((item) => ({
                    id: item.id,
                    name: item.name ?? item.city_name ?? item.title ?? '',
                })) : [];
                setCitiesList(list);

                // If we already have a city name, resolve its id
                const cityName = form.address?.city;
                if (cityName) {
                    const found = list.find((c) => String(c.name).trim().toLowerCase() === String(cityName).trim().toLowerCase());
                    if (found) {
                        setForm((prev) => ({
                            ...prev,
                            address: { ...prev.address, city_id: found.id },
                        }));
                    }
                }
            })
            .catch(() => setCitiesList([]));
    }, [form.address?.state_id]);

    useEffect(() => {
        if (ProfileInfoError) {
            showNotification({ type: 'error', title: 'Error', message: ProfileInfoError?.message || "Failed to load profile information." });
        }
    }, [ProfileInfoError]);

    useEffect(() => {
        if (ProfileAddressError) {
            const msg = String(ProfileAddressError?.message || ProfileAddressError?.data?.error || '').toLowerCase();
            const isNotFound = ProfileAddressError?.status === 404 || msg.includes('profile address not found');
            if (!isNotFound) {
                showNotification({ type: 'error', title: 'Error', message: ProfileAddressError?.message || "Failed to load profile address." });
            }
        }
    }, [ProfileAddressError]);

    useEffect(() => {
        if (SaveProfileError) {
            showNotification({ type: 'error', title: 'Error', message: SaveProfileError?.message || "Failed to update profile information." });
        }
    }, [SaveProfileError]);

    useEffect(() => {
        if (SaveProfileAddressError) {
            showNotification({ type: 'error', title: 'Error', message: SaveProfileAddressError?.message || "Failed to update profile address." });
        }
    }, [SaveProfileAddressError]);

    useEffect(() => {
        if (!SaveProfileData && !SaveProfileAddressData) return;
        showNotification({ type: 'success', title: 'Success', message: "Profile updated." });
        // setShowModal(true);
    }, [SaveProfileData, SaveProfileAddressData]);

    const handleFieldChange = (key) => (event) => {
        const value = event?.target?.value ?? "";
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleAddressChange = (key) => (event) => {
        const value = event?.target?.value ?? "";
        setForm((prev) => ({
            ...prev,
            address: { ...prev.address, [key]: value },
        }));
    };

    const handleCountryChange = (event) => {
        const value = event?.target?.value ?? "";
        const country = countriesList.find((c) => String(c.id) === String(value));
        setForm((prev) => ({
            ...prev,
            address: {
                ...prev.address,
                country_id: value,
                country: country?.name ?? '',
                state_id: '',
                state: '',
                city_id: '',
                city: '',
            },
        }));
    };

    const handleStateChange = (event) => {
        const value = event?.target?.value ?? "";
        const state = statesList.find((s) => String(s.id) === String(value));
        setForm((prev) => ({
            ...prev,
            address: {
                ...prev.address,
                state_id: value,
                state: state?.name ?? '',
                city_id: '',
                city: '',
            },
        }));
    };

    const handleCityChange = (event) => {
        const value = event?.target?.value ?? "";
        const city = citiesList.find((c) => String(c.id) === String(value));
        setForm((prev) => ({
            ...prev,
            address: { ...prev.address, city_id: value, city: city?.name ?? '' },
        }));
    };

    return (
        <>
            <DashboardLayout>
                <div className="dashCard mb-4">
                    <div className="row mb-3">
                        <div className="col-12">
                            <h2 className="mainTitle">
                                {/* <BackButton /> */}
                                 Profile
                            </h2>
                        </div>
                    </div>
                    <div className="row mb-3">
                        {userData ?
                            <div className="col-12">
                                <form className="profile-form">
                                  
                                    <div className="row mb-5">
                                        <div className="col-md-12">
                                            <div className='subHeadTitle'>
                                                <h5>Information </h5>
                                            </div>
                                        </div>
                                        <div className="col-lg-8">


                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput
                                                        label="User Name"
                                                        labelClass="mainLabel"
                                                        required
                                                        type="text"
                                                        placeholder="Enter User Name"
                                                        inputClass="mainInput"
                                                        value={form.username}
                                                        onChange={handleFieldChange("username")}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput
                                                        label="Email Address"
                                                        labelClass="mainLabel"
                                                        type="email"
                                                        placeholder="Email Address"
                                                        inputClass="mainInput"
                                                        value={form.email}
                                                        disabled
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput
                                                        label="First Name"
                                                        labelClass="mainLabel"
                                                        required
                                                        type="text"
                                                        placeholder="Enter First Name"
                                                        inputClass="mainInput"
                                                        value={form.first_name}
                                                        onChange={handleFieldChange("first_name")}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput
                                                        label="Last Name"
                                                        labelClass="mainLabel"
                                                        required
                                                        type="text"
                                                        placeholder="Enter Last Name"
                                                        inputClass="mainInput"
                                                        value={form.last_name}
                                                        onChange={handleFieldChange("last_name")}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput
                                                        label="Display Name"
                                                        labelClass="mainLabel"
                                                        required
                                                        type="text"
                                                        placeholder="Enter Display Name"
                                                        inputClass="mainInput"
                                                        value={form.display_name}
                                                        onChange={handleFieldChange("display_name")}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput
                                                        label="Phone Number"
                                                        labelClass="mainLabel"
                                                        type="tel"
                                                        placeholder="Enter Phone Number"
                                                        inputClass="mainInput"
                                                        value={form.phone}
                                                        onChange={handleFieldChange("phone")}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="mainLabel">Native Language</label>
                                                    <select
                                                        name="native_language"
                                                        id="native_language"
                                                        className="mainInput"
                                                        autoComplete="nope"
                                                        value={form.native_language}
                                                        onChange={handleFieldChange("native_language")}
                                                    >
                                                        <option value="">Native Language</option>
                                                        <option value="af">Afrikaans</option>
                                                        <option value="sq">Albanian - shqip</option>
                                                        <option value="am">Amharic - አማርኛ</option>
                                                        <option value="ar">Arabic - العربية</option>
                                                        <option value="an">Aragonese - aragonés</option>
                                                        <option value="hy">Armenian - հայերեն</option>
                                                        <option value="ast">Asturian - asturianu</option>
                                                        <option value="az">Azerbaijani - azərbaycan dili</option>
                                                        <option value="eu">Basque - euskara</option>
                                                        <option value="be">Belarusian - беларуская</option>
                                                        <option value="bn">Bengali - বাংলা</option>
                                                        <option value="bs">Bosnian - bosanski</option>
                                                        <option value="br">Breton - brezhoneg</option>
                                                        <option value="bg">Bulgarian - български</option>
                                                        <option value="ca">Catalan - català</option>
                                                        <option value="ckb">Central Kurdish - کوردی (دەستنوسی عەرەبی)</option>
                                                        <option value="zh">Chinese - 中文</option>
                                                        <option value="zh-HK">Chinese (Hong Kong) - 中文（香港）</option>
                                                        <option value="zh-CN">Chinese (Simplified) - 中文（简体）</option>
                                                        <option value="zh-TW">Chinese (Traditional) - 中文（繁體）</option>
                                                        <option value="co">Corsican</option>
                                                        <option value="hr">Croatian - hrvatski</option>
                                                        <option value="cs">Czech - čeština</option>
                                                        <option value="da">Danish - dansk</option>
                                                        <option value="nl">Dutch - Nederlands</option>
                                                        <option value="en">English</option>
                                                        <option value="en-AU">English (Australia)</option>
                                                        <option value="en-CA">English (Canada)</option>
                                                        <option value="en-IN">English (India)</option>
                                                        <option value="en-NZ">English (New Zealand)</option>
                                                        <option value="en-ZA">English (South Africa)</option>
                                                        <option value="en-GB">English (United Kingdom)</option>
                                                        <option value="en-US">English (United States)</option>
                                                        <option value="eo">Esperanto - esperanto</option>
                                                        <option value="et">Estonian - eesti</option>
                                                        <option value="fo">Faroese - føroyskt</option>
                                                        <option value="fil">Filipino</option>
                                                        <option value="fi">Finnish - suomi</option>
                                                        <option value="fr">French - français</option>
                                                        <option value="fr-CA">French (Canada) - français (Canada)</option>
                                                        <option value="fr-FR">French (France) - français (France)</option>
                                                        <option value="fr-CH">French (Switzerland) - français (Suisse)</option>
                                                        <option value="gl">Galician - galego</option>
                                                        <option value="ka">Georgian - ქართული</option>
                                                        <option value="de">German - Deutsch</option>
                                                        <option value="de-AT">German (Austria) - Deutsch (Österreich)</option>
                                                        <option value="de-DE">German (Germany) - Deutsch (Deutschland)</option>
                                                        <option value="de-LI">
                                                            German (Liechtenstein) - Deutsch (Liechtenstein)
                                                        </option>
                                                        <option value="de-CH">German (Switzerland) - Deutsch (Schweiz)</option>
                                                        <option value="el">Greek - Ελληνικά</option>
                                                        <option value="gn">Guarani</option>
                                                        <option value="gu">Gujarati - ગુજરાતી</option>
                                                        <option value="ha">Hausa</option>
                                                        <option value="haw">Hawaiian - ʻŌlelo Hawaiʻi</option>
                                                        <option value="he">Hebrew - עברית</option>
                                                        <option value="hi">Hindi - हिन्दी</option>
                                                        <option value="hu">Hungarian - magyar</option>
                                                        <option value="is">Icelandic - íslenska</option>
                                                        <option value="id">Indonesian - Indonesia</option>
                                                        <option value="ia">Interlingua</option>
                                                        <option value="ga">Irish - Gaeilge</option>
                                                        <option value="it">Italian - italiano</option>
                                                        <option value="it-IT">Italian (Italy) - italiano (Italia)</option>
                                                        <option value="it-CH">Italian (Switzerland) - italiano (Svizzera)</option>
                                                        <option value="ja">Japanese - 日本語</option>
                                                        <option value="kn">Kannada - ಕನ್ನಡ</option>
                                                        <option value="kk">Kazakh - қазақ тілі</option>
                                                        <option value="km">Khmer - ខ្មែរ</option>
                                                        <option value="ko">Korean - 한국어</option>
                                                        <option value="ku">Kurdish - Kurdî</option>
                                                        <option value="ky">Kyrgyz - кыргызча</option>
                                                        <option value="lo">Lao - ລາວ</option>
                                                        <option value="la">Latin</option>
                                                        <option value="lv">Latvian - latviešu</option>
                                                        <option value="ln">Lingala - lingála</option>
                                                        <option value="lt">Lithuanian - lietuvių</option>
                                                        <option value="mk">Macedonian - македонски</option>
                                                        <option value="ms">Malay - Bahasa Melayu</option>
                                                        <option value="ml">Malayalam - മലയാളം</option>
                                                        <option value="mt">Maltese - Malti</option>
                                                        <option value="mr">Marathi - मराठी</option>
                                                        <option value="mn">Mongolian - монгол</option>
                                                        <option value="ne">Nepali - नेपाली</option>
                                                        <option value="no">Norwegian - norsk</option>
                                                        <option value="nb">Norwegian Bokmål - norsk bokmål</option>
                                                        <option value="nn">Norwegian Nynorsk - nynorsk</option>
                                                        <option value="oc">Occitan</option>
                                                        <option value="or">Oriya - ଓଡ଼ିଆ</option>
                                                        <option value="om">Oromo - Oromoo</option>
                                                        <option value="ps">Pashto - پښتو</option>
                                                        <option value="fa">Persian - فارسی</option>
                                                        <option value="pl">Polish - polski</option>
                                                        <option value="pt">Portuguese - português</option>
                                                        <option value="pt-BR">Portuguese (Brazil) - português (Brasil)</option>
                                                        <option value="pt-PT">Portuguese (Portugal) - português (Portugal)</option>
                                                        <option value="pa">Punjabi - ਪੰਜਾਬੀ</option>
                                                        <option value="qu">Quechua</option>
                                                        <option value="ro">Romanian - română</option>
                                                        <option value="mo">Romanian (Moldova) - română (Moldova)</option>
                                                        <option value="rm">Romansh - rumantsch</option>
                                                        <option value="ru">Russian - русский</option>
                                                        <option value="gd">Scottish Gaelic</option>
                                                        <option value="sr">Serbian - српски</option>
                                                        <option value="sh">Serbo - Croatian</option>
                                                        <option value="sn">Shona - chiShona</option>
                                                        <option value="sd">Sindhi</option>
                                                        <option value="si">Sinhala - සිංහල</option>
                                                        <option value="sk">Slovak - slovenčina</option>
                                                        <option value="sl">Slovenian - slovenščina</option>
                                                        <option value="so">Somali - Soomaali</option>
                                                        <option value="st">Southern Sotho</option>
                                                        <option value="es">Spanish - español</option>
                                                        <option value="es-AR">Spanish (Argentina) - español (Argentina)</option>
                                                        <option value="es-419">
                                                            Spanish (Latin America) - español (Latinoamérica)
                                                        </option>
                                                        <option value="es-MX">Spanish (Mexico) - español (México)</option>
                                                        <option value="es-ES">Spanish (Spain) - español (España)</option>
                                                        <option value="es-US">
                                                            Spanish (United States) - español (Estados Unidos)
                                                        </option>
                                                        <option value="su">Sundanese</option>
                                                        <option value="sw">Swahili - Kiswahili</option>
                                                        <option value="sv">Swedish - svenska</option>
                                                        <option value="tg">Tajik - тоҷикӣ</option>
                                                        <option value="ta">Tamil - தமிழ்</option>
                                                        <option value="tt">Tatar</option>
                                                        <option value="te">Telugu - తెలుగు</option>
                                                        <option value="th">Thai - ไทย</option>
                                                        <option value="ti">Tigrinya - ትግርኛ</option>
                                                        <option value="to">Tongan - lea fakatonga</option>
                                                        <option value="tr">Turkish - Türkçe</option>
                                                        <option value="tk">Turkmen</option>
                                                        <option value="tw">Twi</option>
                                                        <option value="uk">Ukrainian - українська</option>
                                                        <option value="ur">Urdu - اردو</option>
                                                        <option value="ug">Uyghur</option>
                                                        <option value="uz">Uzbek - o‘zbek</option>
                                                        <option value="vi">Vietnamese - Tiếng Việt</option>
                                                        <option value="wa">Walloon - wa</option>
                                                        <option value="cy">Welsh - Cymraeg</option>
                                                        <option value="fy">Western Frisian</option>
                                                        <option value="xh">Xhosa</option>
                                                        <option value="yi">Yiddish</option>
                                                        <option value="yo">Yoruba - Èdè Yorùbá</option>
                                                        <option value="zu">Zulu - isiZulu</option>{" "}
                                                    </select>

                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <div className="form-groups">
                                                        <label className="mainLabel">Other Language</label>
                                                        <select
                                                            name="other_language"
                                                            id="other_language"
                                                            className="mainInput"
                                                            autoComplete="nope"
                                                            value={form.other_language}
                                                            onChange={handleFieldChange("other_language")}
                                                        >
                                                            <option value="">Native Language</option>
                                                            <option value="af">Afrikaans</option>
                                                            <option value="sq">Albanian - shqip</option>
                                                            <option value="am">Amharic - አማርኛ</option>
                                                            <option value="ar">Arabic - العربية</option>
                                                            <option value="an">Aragonese - aragonés</option>
                                                            <option value="hy">Armenian - հայերեն</option>
                                                            <option value="ast">Asturian - asturianu</option>
                                                            <option value="az">Azerbaijani - azərbaycan dili</option>
                                                            <option value="eu">Basque - euskara</option>
                                                            <option value="be">Belarusian - беларуская</option>
                                                            <option value="bn">Bengali - বাংলা</option>
                                                            <option value="bs">Bosnian - bosanski</option>
                                                            <option value="br">Breton - brezhoneg</option>
                                                            <option value="bg">Bulgarian - български</option>
                                                            <option value="ca">Catalan - català</option>
                                                            <option value="ckb">Central Kurdish - کوردی (دەستنوسی عەرەبی)</option>
                                                            <option value="zh">Chinese - 中文</option>
                                                            <option value="zh-HK">Chinese (Hong Kong) - 中文（香港）</option>
                                                            <option value="zh-CN">Chinese (Simplified) - 中文（简体）</option>
                                                            <option value="zh-TW">Chinese (Traditional) - 中文（繁體）</option>
                                                            <option value="co">Corsican</option>
                                                            <option value="hr">Croatian - hrvatski</option>
                                                            <option value="cs">Czech - čeština</option>
                                                            <option value="da">Danish - dansk</option>
                                                            <option value="nl">Dutch - Nederlands</option>
                                                            <option value="en">English</option>
                                                            <option value="en-AU">English (Australia)</option>
                                                            <option value="en-CA">English (Canada)</option>
                                                            <option value="en-IN">English (India)</option>
                                                            <option value="en-NZ">English (New Zealand)</option>
                                                            <option value="en-ZA">English (South Africa)</option>
                                                            <option value="en-GB">English (United Kingdom)</option>
                                                            <option value="en-US">English (United States)</option>
                                                            <option value="eo">Esperanto - esperanto</option>
                                                            <option value="et">Estonian - eesti</option>
                                                            <option value="fo">Faroese - føroyskt</option>
                                                            <option value="fil">Filipino</option>
                                                            <option value="fi">Finnish - suomi</option>
                                                            <option value="fr">French - français</option>
                                                            <option value="fr-CA">French (Canada) - français (Canada)</option>
                                                            <option value="fr-FR">French (France) - français (France)</option>
                                                            <option value="fr-CH">French (Switzerland) - français (Suisse)</option>
                                                            <option value="gl">Galician - galego</option>
                                                            <option value="ka">Georgian - ქართული</option>
                                                            <option value="de">German - Deutsch</option>
                                                            <option value="de-AT">German (Austria) - Deutsch (Österreich)</option>
                                                            <option value="de-DE">German (Germany) - Deutsch (Deutschland)</option>
                                                            <option value="de-LI">
                                                                German (Liechtenstein) - Deutsch (Liechtenstein)
                                                            </option>
                                                            <option value="de-CH">German (Switzerland) - Deutsch (Schweiz)</option>
                                                            <option value="el">Greek - Ελληνικά</option>
                                                            <option value="gn">Guarani</option>
                                                            <option value="gu">Gujarati - ગુજરાતી</option>
                                                            <option value="ha">Hausa</option>
                                                            <option value="haw">Hawaiian - ʻŌlelo Hawaiʻi</option>
                                                            <option value="he">Hebrew - עברית</option>
                                                            <option value="hi">Hindi - हिन्दी</option>
                                                            <option value="hu">Hungarian - magyar</option>
                                                            <option value="is">Icelandic - íslenska</option>
                                                            <option value="id">Indonesian - Indonesia</option>
                                                            <option value="ia">Interlingua</option>
                                                            <option value="ga">Irish - Gaeilge</option>
                                                            <option value="it">Italian - italiano</option>
                                                            <option value="it-IT">Italian (Italy) - italiano (Italia)</option>
                                                            <option value="it-CH">Italian (Switzerland) - italiano (Svizzera)</option>
                                                            <option value="ja">Japanese - 日本語</option>
                                                            <option value="kn">Kannada - ಕನ್ನಡ</option>
                                                            <option value="kk">Kazakh - қазақ тілі</option>
                                                            <option value="km">Khmer - ខ្មែរ</option>
                                                            <option value="ko">Korean - 한국어</option>
                                                            <option value="ku">Kurdish - Kurdî</option>
                                                            <option value="ky">Kyrgyz - кыргызча</option>
                                                            <option value="lo">Lao - ລາວ</option>
                                                            <option value="la">Latin</option>
                                                            <option value="lv">Latvian - latviešu</option>
                                                            <option value="ln">Lingala - lingála</option>
                                                            <option value="lt">Lithuanian - lietuvių</option>
                                                            <option value="mk">Macedonian - македонски</option>
                                                            <option value="ms">Malay - Bahasa Melayu</option>
                                                            <option value="ml">Malayalam - മലയാളം</option>
                                                            <option value="mt">Maltese - Malti</option>
                                                            <option value="mr">Marathi - मराठी</option>
                                                            <option value="mn">Mongolian - монгол</option>
                                                            <option value="ne">Nepali - नेपाली</option>
                                                            <option value="no">Norwegian - norsk</option>
                                                            <option value="nb">Norwegian Bokmål - norsk bokmål</option>
                                                            <option value="nn">Norwegian Nynorsk - nynorsk</option>
                                                            <option value="oc">Occitan</option>
                                                            <option value="or">Oriya - ଓଡ଼ିଆ</option>
                                                            <option value="om">Oromo - Oromoo</option>
                                                            <option value="ps">Pashto - پښتو</option>
                                                            <option value="fa">Persian - فارسی</option>
                                                            <option value="pl">Polish - polski</option>
                                                            <option value="pt">Portuguese - português</option>
                                                            <option value="pt-BR">Portuguese (Brazil) - português (Brasil)</option>
                                                            <option value="pt-PT">Portuguese (Portugal) - português (Portugal)</option>
                                                            <option value="pa">Punjabi - ਪੰਜਾਬੀ</option>
                                                            <option value="qu">Quechua</option>
                                                            <option value="ro">Romanian - română</option>
                                                            <option value="mo">Romanian (Moldova) - română (Moldova)</option>
                                                            <option value="rm">Romansh - rumantsch</option>
                                                            <option value="ru">Russian - русский</option>
                                                            <option value="gd">Scottish Gaelic</option>
                                                            <option value="sr">Serbian - српски</option>
                                                            <option value="sh">Serbo - Croatian</option>
                                                            <option value="sn">Shona - chiShona</option>
                                                            <option value="sd">Sindhi</option>
                                                            <option value="si">Sinhala - සිංහල</option>
                                                            <option value="sk">Slovak - slovenčina</option>
                                                            <option value="sl">Slovenian - slovenščina</option>
                                                            <option value="so">Somali - Soomaali</option>
                                                            <option value="st">Southern Sotho</option>
                                                            <option value="es">Spanish - español</option>
                                                            <option value="es-AR">Spanish (Argentina) - español (Argentina)</option>
                                                            <option value="es-419">
                                                                Spanish (Latin America) - español (Latinoamérica)
                                                            </option>
                                                            <option value="es-MX">Spanish (Mexico) - español (México)</option>
                                                            <option value="es-ES">Spanish (Spain) - español (España)</option>
                                                            <option value="es-US">
                                                                Spanish (United States) - español (Estados Unidos)
                                                            </option>
                                                            <option value="su">Sundanese</option>
                                                            <option value="sw">Swahili - Kiswahili</option>
                                                            <option value="sv">Swedish - svenska</option>
                                                            <option value="tg">Tajik - тоҷикӣ</option>
                                                            <option value="ta">Tamil - தமிழ்</option>
                                                            <option value="tt">Tatar</option>
                                                            <option value="te">Telugu - తెలుగు</option>
                                                            <option value="th">Thai - ไทย</option>
                                                            <option value="ti">Tigrinya - ትግርኛ</option>
                                                            <option value="to">Tongan - lea fakatonga</option>
                                                            <option value="tr">Turkish - Türkçe</option>
                                                            <option value="tk">Turkmen</option>
                                                            <option value="tw">Twi</option>
                                                            <option value="uk">Ukrainian - українська</option>
                                                            <option value="ur">Urdu - اردو</option>
                                                            <option value="ug">Uyghur</option>
                                                            <option value="uz">Uzbek - o‘zbek</option>
                                                            <option value="vi">Vietnamese - Tiếng Việt</option>
                                                            <option value="wa">Walloon - wa</option>
                                                            <option value="cy">Welsh - Cymraeg</option>
                                                            <option value="fy">Western Frisian</option>
                                                            <option value="xh">Xhosa</option>
                                                            <option value="yi">Yiddish</option>
                                                            <option value="yo">Yoruba - Èdè Yorùbá</option>
                                                            <option value="zu">Zulu - isiZulu</option>{" "}
                                                        </select>
                                                    </div>

                                                </div>

                                                <div className='mb-3 col-md-12'>
                                                    <div className="form-group">
                                                        <label htmlFor="description" className='mainLabel'>Bio</label>
                                                        <textarea
                                                            className='mainInput'
                                                            name='bio'
                                                            value={form.bio}
                                                            onChange={handleFieldChange("bio")}
                                                        ></textarea>

                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                        <div className="col-12">
                                            <CustomButton
                                                type="button"
                                                variant="primaryButton"
                                                className="me-3 mb-2"
                                                text={SaveProfileLoading ? "Saving..." : "Save"}
                                                onClick={handleClickPopup}
                                                disabled={SaveProfileLoading}
                                            />

                                        </div>

                                    </div>

                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className='subHeadTitle'>
                                                <h5>Address </h5>
                                            </div>
                                        </div>
                                        <div className="col-lg-8">
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput label="Street Address" labelClass="mainLabel" required type="text" placeholder="Enter Street Address" inputClass="mainInput" value={form.address.street_address} onChange={handleAddressChange('street_address')} />
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <CustomInput label="Apt, Suite" labelClass="mainLabel" required type="text" placeholder="Enter Apt, Suite" inputClass="mainInput" value={form.address.apt_suite} onChange={handleAddressChange('apt_suite')} />
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <SelectBox selectClass="mainInput" name="Select Country" label="Country" required option={countriesList} value={form.address.country_id} onChange={handleCountryChange} placeholder="Select Country"
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <SelectBox selectClass="mainInput" name="Select State" label="State" required option={statesList} value={form.address.state_id} onChange={handleStateChange} placeholder="Select State" />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <SelectBox selectClass="mainInput" name="Select City" label="City" required option={citiesList} value={form.address.city_id} onChange={handleCityChange} placeholder="Select City" />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput label="Postal/Zip Code*" labelClass="mainLabel" required type="number" placeholder="Enter Postal/Zip Code*" inputClass="mainInput" value={form.address.zip_code} onChange={handleAddressChange('zip_code')} />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <CustomInput label="Neighborhood" labelClass="mainLabel" required type="text" placeholder="Enter Neighborhood" inputClass="mainInput" value={form.address.neighborhood} onChange={handleAddressChange('neighborhood')} />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <CustomButton
                                                type="button"
                                                variant="primaryButton"
                                                className="me-3 mb-2"
                                                text={SaveProfileLoading ? "Saving..." : "Save"}
                                                onClick={handleClickPopup}
                                                disabled={SaveProfileLoading}
                                            />

                                        </div>

                                    </div>
                                </form>
                            </div> : ''}

                    </div>
                </div>
                <CustomModal show={showModal} close={handleClose} success heading='Your profile is Successfully Updated! Continue' />
            </DashboardLayout>
        </>
    );
};

export default EditProfile;
